import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Film, LogOut, Plus, Link2, Upload, Loader2, ArrowUpRight, Home, Car, Camera, Sparkles, Wand2, Trash2, Plane, Pencil, Check, Download, PackageOpen, Utensils, Wrench, Layers, Users } from "lucide-react";
import { exportProject, importProject } from "@/lib/transfer";

const VERTICALS = [
  { v: "real_estate", label: "Real Estate", icon: Home },
  { v: "dealership", label: "Dealership", icon: Car },
  { v: "restaurant", label: "Restaurant", icon: Utensils },
  { v: "trades", label: "Trades", icon: Wrench },
  { v: "photographer", label: "Photographer", icon: Camera },
  { v: "general", label: "General", icon: Sparkles },
];
const TONES = [
  { v: "energetic", label: "Energetic Walkaround" },
  { v: "premium", label: "Premium Showroom" },
];

const MODELS = [
  { v: "mini", label: "Seedance 2.0 Mini" },
  { v: "fast", label: "Seedance 2.0 Fast" },
  { v: "pro", label: "Seedance 2.0 Pro" },
  { v: "v25", label: "Seedance 2.5 (newest)" },
  { v: "veo", label: "Google Veo 3.1 (8s, beta)" },
];
const RES = ["480", "720", "1080"];
const ASPECTS = [
  { v: "9:16", label: "9:16 Vertical" },
  { v: "16:9", label: "16:9 Horizontal" },
];
const MUSIC = [
  { v: "cinematic", label: "Cinematic" },
  { v: "uplifting", label: "Uplifting" },
  { v: "luxury", label: "Luxury Lounge" },
  { v: "none", label: "No Music" },
];
const VOICE = [
  { v: "on", label: "Voiceover On" },
  { v: "off", label: "No Voiceover" },
];
const TRANSITIONS = [
  { v: "dip", label: "Dip to Black" },
  { v: "hard", label: "Hard Cut" },
  { v: "dissolve", label: "Smooth Dissolve" },
];

const STATUS_STYLES = {
  done: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  running: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  error: "text-red-400 border-red-500/40 bg-red-500/10",
  needs_manual: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  created: "text-zinc-400 border-zinc-700 bg-zinc-800/40",
};

const StatusBadge = ({ status }) => (
  <span
    className={`font-mono2 text-[10px] uppercase tracking-[0.15em] px-2 py-1 border rounded-sm ${
      STATUS_STYLES[status] || STATUS_STYLES.created
    }`}
  >
    {(status || "created").replace("_", " ")}
  </span>
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [url, setUrl] = useState("");
  const [vertical, setVertical] = useState("real_estate");
  const [listFilter, setListFilter] = useState("all");
  const [tone, setTone] = useState("energetic");
  const [mode, setMode] = useState("auto");
  const [model, setModel] = useState("mini");
  const [resolution, setResolution] = useState("720");
  const [aspect, setAspect] = useState("9:16");
  const [music, setMusic] = useState("cinematic");
  const [voice, setVoice] = useState("on");
  const [transition, setTransition] = useState("dip");
  const [useAgent, setUseAgent] = useState(true);
  const [motionMode, setMotionMode] = useState("glide");
  const [bizType, setBizType] = useState("");
  const [focus, setFocus] = useState("");
  const [cta, setCta] = useState("");
  const [briefBusy, setBriefBusy] = useState(false);

  const assistBrief = async () => {
    setBriefBusy(true);
    try {
      const { data } = await api.post("/projects/brief/assist", {
        business_type: bizType, focus, cta, url, vertical,
      });
      setBizType(data.business_type || bizType);
      setFocus(data.focus || focus);
      setCta(data.cta || cta);
      toast.success("Brief drafted — tweak anything you like");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI assist failed");
    } finally {
      setBriefBusy(false);
    }
  };
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [importing, setImporting] = useState(false);
  const [importPct, setImportPct] = useState(0);
  const [exportingId, setExportingId] = useState(null);
  const importInputRef = React.useRef(null);

  const onExport = async (e, p) => {
    e.stopPropagation();
    setExportingId(p.id);
    toast.info("Packing project… this can take a moment for reels with many clips");
    try {
      await exportProject(p.id);
      toast.success("Project pack downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    } finally {
      setExportingId(null);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportPct(0);
    try {
      const res = await importProject(file, setImportPct);
      toast.success("Project imported with all its clips");
      load();
      if (res.project_id) navigate(`/project/${res.project_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      setImportPct(0);
    }
  };

  const renameProject = async (e, p) => {
    e.stopPropagation();
    const name = editDraft.trim();
    try {
      await api.post(`/projects/${p.id}/settings`, { name });
      setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, name } : x)));
      setEditingId(null);
      toast.success("Project renamed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/projects/voices").then((r) => {
      setVoices(r.data.voices || []);
      setVoiceName(r.data.default || (r.data.voices?.[0]?.key ?? ""));
    }).catch(() => {});
  }, []);

  const isDealer = vertical === "dealership";
  const isPhotographer = vertical === "photographer";
  const isGeneral = vertical === "general";
  const isRestaurant = vertical === "restaurant";
  const isTrades = vertical === "trades";

  const previewVoice = async () => {
    if (!voiceName) return;
    setPreviewing(true);
    try {
      const res = await api.post("/projects/voices/preview",
        { voice: voiceName, text: isDealer
            ? "Take a look at this incredible ride. Let me walk you around it."
            : isPhotographer
              ? "Here's a look back at a day full of love and light."
              : "Welcome to this beautiful home. Let me show you around." },
        { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(res.data));
      audio.play();
    } catch (err) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const createCards = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { kind: "cards", aspect, music, voice_name: voiceName });
      toast.success("Cards reel created — add your cards");
      navigate(`/project/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { url, vertical, tone, mode, model, resolution, aspect, music, voice: voice === "on", voice_name: voiceName, transition, use_agent: useAgent, motion_mode: motionMode, business_type: bizType, focus, cta });
      if (mode === "auto") {
        await api.post(`/projects/${data.id}/run`);
      }
      toast.success("Project created");
      navigate(`/project/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Delete this project?\n\n${p.final?.title || p.url || "Manual project"}\n\nThis can't be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Project deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 relative z-10">
      <header className="sticky top-0 z-20 bg-zinc-950/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-500">
            <Film className="w-5 h-5" />
            <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-zinc-200">
              Reel Studio
            </span>
          </div>
          <div className="flex items-center gap-4">
            <input ref={importInputRef} type="file" accept=".zip,application/zip" className="hidden" data-testid="import-file-input" onChange={onImportFile} />
            <button
              data-testid="import-project-button"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:bg-white/5 transition-colors disabled:opacity-50"
              title="Import a .reelpack.zip exported from another environment"
            >
              {importing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Importing {importPct}%</> : <><PackageOpen className="w-3.5 h-3.5" /> Import</>}
            </button>
            <button
              data-testid="nav-agents"
              onClick={() => navigate("/agents")}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:bg-white/5 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" /> Agents
            </button>
            {user?.role === "admin" && (
              <button
                data-testid="nav-users"
                onClick={() => navigate("/team")}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:bg-white/5 transition-colors"
              >
                <Users className="w-3.5 h-3.5" /> Users
              </button>
            )}
            <button
              data-testid="nav-flythrough"
              onClick={() => navigate("/flythrough")}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-400 hover:border-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              <Plane className="w-3.5 h-3.5" /> Flythrough
              <span className="text-[9px] uppercase tracking-widest text-amber-500/70">beta</span>
            </button>
            <button
              data-testid="nav-cards-reel"
              onClick={createCards}
              disabled={creating}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-400 hover:border-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
              title="Create a video made purely of narrated info cards — no property footage needed"
            >
              <Layers className="w-3.5 h-3.5" /> Cards Reel
            </button>
            <span className="text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-zinc-400 hover:text-white rounded-sm"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* New project */}
        <section className="fade-up">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-amber-500">New Project</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 mb-6 max-w-2xl">
            {isDealer ? "Paste a vehicle listing. Get a cinematic reel."
              : isRestaurant ? "Paste your restaurant. Get a mouth-watering reel."
              : isTrades ? "Paste your trades site. Show off your best work."
              : isPhotographer ? "Paste your portfolio. Get a cinematic highlight reel."
              : isGeneral ? "Paste a social or Maps link. Get a promo reel."
              : "Paste a listing. Get a cinematic reel."}
          </h1>

          {/* Vertical switch */}
          <div className="flex gap-2 mb-8" data-testid="vertical-switch">
            {VERTICALS.map((vt) => (
              <button
                key={vt.v}
                data-testid={`vertical-${vt.v}`}
                onClick={() => setVertical(vt.v)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm border transition-colors ${
                  vertical === vt.v
                    ? "border-amber-500 bg-amber-500/10 text-amber-400"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                }`}
              >
                <vt.icon className="w-4 h-4" />
                {vt.label}
              </button>
            ))}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 sm:p-8">
            <div className="flex gap-2 mb-6">
              {[
                { v: "auto", label: "From URL", icon: Link2 },
                { v: "manual", label: "Manual / Upload", icon: Upload },
              ].map((m) => (
                <button
                  key={m.v}
                  data-testid={`mode-${m.v}`}
                  onClick={() => setMode(m.v)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm border transition-colors ${
                    mode === m.v
                      ? "border-amber-500 bg-amber-500/10 text-amber-400"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <m.icon className="w-4 h-4" />
                  {m.label}
                </button>
              ))}
            </div>

            {mode === "auto" && (
              <div className="mb-6">
                <Input
                  data-testid="listing-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={isDealer
                    ? "https://www.autotrader.ca/... or cars.com / dealer site"
                    : isRestaurant
                      ? "https://yourrestaurant.com  ·  Instagram  ·  Google Maps link"
                    : isTrades
                      ? "https://yourtradesbusiness.com  ·  Instagram  ·  Google Maps link"
                    : isPhotographer
                      ? "https://yourstudio.com/galleries  (portfolio or album hub page)"
                      : isGeneral
                        ? "https://instagram.com/yourbusiness  ·  facebook page  ·  Google Maps link"
                        : "https://www.realtor.ca/real-estate/..."}
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-12 font-mono2 text-sm focus-visible:ring-amber-500/40 focus-visible:border-amber-500"
                />
                {isDealer && (
                  <p className="mt-2 text-xs text-zinc-500">
                    We'll try to auto-scrape the vehicle. If the site blocks us, you'll paste the details and upload photos on the next screen.
                  </p>
                )}
                {isPhotographer && (
                  <p className="mt-2 text-xs text-zinc-500">
                    Paste a gallery or a portfolio hub page — we'll pool photos across your albums so you can pick the best from several shoots.
                  </p>
                )}
                {isRestaurant && (
                  <p className="mt-2 text-xs text-zinc-500">
                    Paste your website, Instagram or Google Maps — we'll pull the food & interior photos so you can pick the tastiest shots.
                  </p>
                )}
                {isTrades && (
                  <p className="mt-2 text-xs text-zinc-500">
                    Paste your website, Instagram or Google Maps — we'll pull your job photos so you can pick the best finished-work and before/after shots.
                  </p>
                )}
                <p className="mt-2 text-xs text-zinc-500">
                  Or paste an <span className="text-amber-500">Instagram</span>, <span className="text-amber-500">Facebook</span>, <span className="text-amber-500">Google Maps</span> or <span className="text-amber-500">iGuide tour</span> URL — we'll pull the photos for you to pick from.
                </p>
              </div>
            )}
            {mode === "manual" && (
              <p className="mb-6 text-sm text-zinc-500">
                {isDealer
                  ? "You'll paste the vehicle details and upload photos on the next screen."
                  : "You'll paste listing details and upload photos on the next screen."}
              </p>
            )}

            <div className="mb-6 border border-zinc-800 rounded-sm p-4 bg-zinc-950/40" data-testid="campaign-brief">
              <div className="flex items-center justify-between mb-1">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Campaign Brief</p>
                <button
                  type="button"
                  data-testid="brief-ai-assist"
                  onClick={assistBrief}
                  disabled={briefBusy}
                  className="flex items-center gap-1.5 text-xs text-amber-500 hover:text-amber-400 border border-amber-500/40 hover:border-amber-400 rounded-sm px-2.5 py-1 transition-colors disabled:opacity-50"
                >
                  {briefBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  {briefBusy ? "Writing..." : "Write it for me"}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                Tell the AI what you're actually selling and the goal — it steers what the photos are described as, what the script pitches, and the ending call-to-action. Not sure what to write? Tap "Write it for me". Leave blank for a generic reel.
              </p>
              <div className="grid gap-3">
                <Input
                  data-testid="brief-business-type"
                  value={bizType}
                  onChange={(e) => setBizType(e.target.value)}
                  placeholder="Business type — e.g. Landscaper, Optician, Café"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-11 text-sm"
                />
                <Textarea
                  data-testid="brief-focus"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  placeholder="What you're promoting / focus — e.g. Expert lawn installs & retaining walls. Push fall cleanups and sprinkler blowouts. Talk about the quality of the work, not the scenery."
                  className="bg-zinc-950 border-zinc-800 rounded-sm text-sm min-h-20"
                />
                <Input
                  data-testid="brief-cta"
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  placeholder="Call to action — e.g. Book your fall cleanup, call today!"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-11 text-sm"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Model
                </label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="model-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {MODELS.map((m) => (
                      <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Resolution
                </label>
                <Select value={resolution} onValueChange={setResolution}>
                  <SelectTrigger data-testid="resolution-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {RES.map((r) => (
                      <SelectItem key={r} value={r}>{r}p</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Aspect
                </label>
                <Select value={aspect} onValueChange={setAspect}>
                  <SelectTrigger data-testid="aspect-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {ASPECTS.map((a) => (
                      <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Voiceover
                </label>
                <Select value={voice} onValueChange={setVoice}>
                  <SelectTrigger data-testid="voice-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {VOICE.map((v) => (
                      <SelectItem key={v.v} value={v.v}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Background Music
                </label>
                <Select value={music} onValueChange={setMusic}>
                  <SelectTrigger data-testid="music-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {MUSIC.map((m) => (
                      <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Cut Style
                </label>
                <Select value={transition} onValueChange={setTransition}>
                  <SelectTrigger data-testid="transition-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {TRANSITIONS.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Intro Presenter
                </label>
                <Select value={useAgent ? "on" : "off"} onValueChange={(v) => setUseAgent(v === "on")}>
                  <SelectTrigger data-testid="use-agent-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="on">Talking Avatar Intro</SelectItem>
                    <SelectItem value="off">{isPhotographer ? "Straight to Montage" : "No Presenter"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4" data-testid="motion-row">
              <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                Photo Motion
              </label>
              <Select value={motionMode} onValueChange={setMotionMode}>
                <SelectTrigger data-testid="motion-mode-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="glide">Faithful glide (free, default) — your real photo, gentle push-in, nothing redrawn</SelectItem>
                  <SelectItem value="static">Faithful still (free) — your real photo held, zero movement</SelectItem>
                  <SelectItem value="ai">AI motion (Seedance) — cinematic movement, but REDRAWS the photo (subject may change)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] text-zinc-500">
                Faithful modes use your exact photos with <span className="text-amber-400">no AI redraw</span> and cost no credits — the subject (e.g. a horse) stays pixel-identical. Only pick AI motion if you're OK with the photo being re-generated.
              </p>
            </div>

            {isDealer && (
              <div className="mt-4" data-testid="tone-row">
                <label className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  Script Tone
                </label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select" className="mt-2 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {TONES.map((t) => (
                      <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {voice === "on" && (
              <input type="hidden" data-testid="voice-name-hidden" value={voiceName} readOnly />
            )}

            <Button
              data-testid="create-project-button"
              onClick={create}
              disabled={creating || (mode === "auto" && !url.trim())}
              className="mt-6 h-11 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create & Run</>}
            </Button>
          </div>
        </section>

        {/* Projects */}
        <section className="mt-16">
          {(() => {
            const shown = projects.filter((p) =>
              listFilter === "all" ? true : (p.vertical || "real_estate") === listFilter);
            const FILTERS = [
              { v: "all", label: `All (${projects.length})` },
              { v: "real_estate", label: "Real Estate" },
              { v: "dealership", label: "Dealership" },
              { v: "restaurant", label: "Restaurant" },
              { v: "trades", label: "Trades" },
              { v: "photographer", label: "Photographer" },
              { v: "general", label: "General" },
            ];
            return (
              <>
                <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                  <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                    Your Projects ({shown.length})
                  </p>
                  <div className="flex gap-1.5" data-testid="project-list-filter">
                    {FILTERS.map((f) => (
                      <button
                        key={f.v}
                        data-testid={`list-filter-${f.v}`}
                        onClick={() => setListFilter(f.v)}
                        className={`font-mono2 text-[10px] uppercase tracking-[0.15em] rounded-sm px-3 py-1.5 border transition-colors ${
                          listFilter === f.v
                            ? "border-amber-500 bg-amber-500/10 text-amber-500"
                            : "border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300"
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                {shown.length === 0 ? (
                  <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-600">
                    No projects here yet. Create your first reel above.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {shown.map((p) => (
                      <div
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        data-testid={`project-card-${p.id}`}
                        onClick={() => navigate(`/project/${p.id}`)}
                        onKeyDown={(e) => e.key === "Enter" && navigate(`/project/${p.id}`)}
                        className="group relative text-left bg-zinc-900 border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-colors cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={p.status} />
                            <span className="flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-[0.15em] text-zinc-500 border border-zinc-700 rounded-sm px-1.5 py-1">
                              {(p.vertical || "real_estate") === "dealership" ? <Car className="w-3 h-3" />
                                : (p.vertical === "restaurant") ? <Utensils className="w-3 h-3" />
                                : (p.vertical === "trades") ? <Wrench className="w-3 h-3" />
                                : (p.vertical === "photographer") ? <Camera className="w-3 h-3" />
                                : (p.vertical === "general") ? <Sparkles className="w-3 h-3" />
                                : <Home className="w-3 h-3" />}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`rename-project-${p.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingId(p.id);
                                setEditDraft(p.name || p.final?.title || "");
                              }}
                              title="Rename project"
                              className="text-zinc-600 hover:text-amber-500 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              data-testid={`export-project-${p.id}`}
                              onClick={(e) => onExport(e, p)}
                              disabled={exportingId === p.id}
                              title="Export project (download a .reelpack to import elsewhere)"
                              className="text-zinc-600 hover:text-amber-500 transition-colors disabled:opacity-50"
                            >
                              {exportingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </button>
                            <button
                              data-testid={`delete-project-${p.id}`}
                              onClick={(e) => deleteProject(e, p)}
                              title="Delete project"
                              className="text-zinc-600 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-500 transition-colors" />
                          </div>
                        </div>
                        {editingId === p.id ? (
                          <div className="mt-4 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              data-testid={`rename-input-${p.id}`}
                              autoFocus
                              value={editDraft}
                              onChange={(e) => setEditDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") renameProject(e, p);
                                if (e.key === "Escape") setEditingId(null);
                              }}
                              placeholder="Project name"
                              className="flex-1 min-w-0 bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-amber-500"
                            />
                            <button
                              data-testid={`rename-save-${p.id}`}
                              onClick={(e) => renameProject(e, p)}
                              className="text-emerald-400 hover:text-emerald-300 shrink-0"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-4 text-sm font-medium text-zinc-200 truncate">
                            {p.name || p.final?.title || p.url || "Manual project"}
                          </p>
                        )}
                        <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.15em] text-zinc-600">
                          {p.settings?.model} · {p.settings?.resolution}p · {p.settings?.aspect} · {p.stage}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </section>
      </main>
    </div>
  );
}
